import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useBrands } from "../contexts/BrandContext";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { CalendarDays, Clock, MapPin, Building2, Tag, Download } from "lucide-react";
import { formatDateFr } from "../lib/dateFormat";
import { effectiveShiftHours } from "../lib/shiftHours";

const pad2 = (n) => String(n).padStart(2, "0");
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/**
 * Historique complet de planification d'un collaborateur :
 * - Sélecteur de période (par défaut : année en cours)
 * - KPIs : Shifts planifiés + Heures planifiées
 * - Liste chronologique (du plus récent au plus ancien) avec date / horaires /
 *   département / site / catégorie / poste, badge À venir / Passé.
 *
 * S'appuie sur l'endpoint existant GET /api/shifts?collaborateur_id=...&start_date=...&end_date=...
 */
export default function CollaborateurPlanningHistory({ collaborateurId }) {
  const { brands } = useBrands();
  const year = new Date().getFullYear();
  const [range, setRange] = useState({
    start_date: `${year}-01-01`,
    end_date: `${year}-12-31`,
  });
  const [shifts, setShifts] = useState([]);
  const [sites, setSites] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const today = todayISO();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!collaborateurId) return;
      setLoading(true);
      try {
        const [shRes, sitesRes, catsRes] = await Promise.all([
          api.get("/shifts", {
            params: { collaborateur_id: collaborateurId, start_date: range.start_date, end_date: range.end_date },
          }),
          api.get("/sites").catch(() => ({ data: [] })),
          api.get("/shift-categories").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setShifts(shRes.data || []);
        setSites(sitesRes.data || []);
        setCategories(catsRes.data || []);
      } catch {
        if (!cancelled) setShifts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [collaborateurId, range.start_date, range.end_date]);

  const { upcoming, past, totalHours } = useMemo(() => {
    const sorted = [...shifts].sort((a, b) => {
      const k = (b.date || "").localeCompare(a.date || "");
      return k !== 0 ? k : (b.start_time || "").localeCompare(a.start_time || "");
    });
    let h = 0;
    sorted.forEach((s) => { h += effectiveShiftHours(s); });
    return {
      upcoming: sorted.filter((s) => (s.date || "") >= today),
      past: sorted.filter((s) => (s.date || "") < today),
      totalHours: Math.round(h * 10) / 10,
    };
  }, [shifts, today]);

  const enrich = (s) => {
    const brand = brands.find((b) => b.id === s.brand_id);
    const site = sites.find((x) => x.id === s.site_id);
    const category = categories.find((c) => c.id === s.shift_category_id);
    return { brand, site, category };
  };

  const handleExportCsv = () => {
    const escapeCsv = (v) => {
      const s = v == null ? "" : String(v);
      return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = [
      "Date",
      "Heure début",
      "Heure fin",
      "Heures",
      "Département",
      "Site",
      "Catégorie",
      "Poste",
      "Statut",
    ];
    const sorted = [...shifts].sort((a, b) => {
      const k = (a.date || "").localeCompare(b.date || "");
      return k !== 0 ? k : (a.start_time || "").localeCompare(b.start_time || "");
    });
    const rows = sorted.map((s) => {
      const { brand, site, category } = enrich(s);
      const vs = (s.validation_status || "pending").toLowerCase();
      const validationLabel = vs === "completed" ? "Effectué" : vs === "missed" ? "Non effectué" : "";
      return [
        s.date || "",
        s.start_time || "",
        s.end_time || "",
        String(effectiveShiftHours(s)).replace(".", ","),
        brand?.name || "",
        site?.name || "",
        category?.name || "",
        s.role_label || s.poste || "",
        ((s.date || "") >= today ? "À venir" : "Passé") + (validationLabel ? ` · ${validationLabel}` : ""),
      ];
    });
    const csv = [header, ...rows].map((r) => r.map(escapeCsv).join(";")).join("\n");
    const bom = "\uFEFF"; // UTF-8 BOM pour Excel/Numbers FR
    const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ma-planification_${range.start_date}_${range.end_date}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderRow = (s, isPast) => {
    const { brand, site, category } = enrich(s);
    const vs = (s.validation_status || "pending").toLowerCase();
    const validationBadge = vs === "completed"
      ? { label: "Effectué", cls: "bg-emerald-100 text-emerald-800 border border-emerald-200" }
      : vs === "missed"
        ? { label: "Non effectué", cls: "bg-rose-100 text-rose-800 border border-rose-200" }
        : null;
    return (
      <div
        key={s.id}
        className={`p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${isPast ? "bg-white" : "bg-sky-50/30"}`}
        data-testid={`planning-row-${s.id}`}
      >
        <div className={`h-12 w-12 rounded-sm flex flex-col items-center justify-center flex-shrink-0 border ${
          isPast ? "bg-zinc-50 border-zinc-200 text-zinc-500" : "bg-sky-50 border-sky-200 text-sky-700"
        }`}>
          <span className="text-[10px] font-mono leading-none">
            {s.date ? new Date(s.date + "T12:00:00").toLocaleDateString("fr-FR", { month: "short" }).replace(".", "") : "—"}
          </span>
          <span className="text-base font-bold leading-tight">{s.date ? s.date.slice(8, 10) : "—"}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-zinc-900">{formatDateFr(s.date)}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-1 text-xs font-mono text-zinc-600">
            <p className="inline-flex items-center gap-1.5">
              <Clock size={11} className="text-zinc-400" /> {s.start_time} → {s.end_time}
            </p>
            <p className="inline-flex items-center gap-1.5">
              <Building2 size={11} className="text-zinc-400" /> {brand?.name || "—"}
            </p>
            <p className="inline-flex items-center gap-1.5">
              <MapPin size={11} className="text-zinc-400" /> {site?.name || "Site non précisé"}
            </p>
            <p className="inline-flex items-center gap-1.5">
              <Tag size={11} className="text-zinc-400" />
              {category ? (
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: category.color || "#0055FF" }} />
                  {category.name}
                </span>
              ) : (
                <span className="text-zinc-400">Sans catégorie</span>
              )}
            </p>
          </div>
          {(s.role_label || s.poste) && (
            <p className="text-[11px] text-zinc-500 mt-1">Poste : {s.role_label || s.poste}</p>
          )}
        </div>
        <span
          className={`text-[10px] uppercase font-mono tracking-wider px-2 py-0.5 rounded-sm flex-shrink-0 ${
            isPast ? "bg-zinc-100 text-zinc-600" : "bg-sky-100 text-sky-700"
          }`}
        >
          {isPast ? "Passé" : "À venir"}
        </span>
        {validationBadge && (
          <span
            className={`text-[10px] uppercase font-mono tracking-wider px-2 py-0.5 rounded-sm flex-shrink-0 ${validationBadge.cls}`}
            data-testid={`planning-row-validation-${s.id}`}
          >
            {validationBadge.label}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="collab-planning-history">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label className="label-uppercase mb-1.5 block">Période — du</Label>
          <Input
            type="date"
            value={range.start_date}
            onChange={(e) => setRange({ ...range, start_date: e.target.value })}
            data-testid="planning-start-date"
          />
        </div>
        <div>
          <Label className="label-uppercase mb-1.5 block">au</Label>
          <Input
            type="date"
            value={range.end_date}
            onChange={(e) => setRange({ ...range, end_date: e.target.value })}
            data-testid="planning-end-date"
          />
        </div>
        <div className="ml-auto">
          <Button
            variant="outline"
            onClick={handleExportCsv}
            disabled={shifts.length === 0 || loading}
            data-testid="planning-export-csv"
            title="Télécharger la planification au format CSV (compatible Excel)"
          >
            <Download size={14} className="mr-1.5" />
            Télécharger ma planification
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid="planning-kpi-count">
          <div className="flex items-center gap-2 mb-2">
            <CalendarDays size={14} className="text-blue-600" />
            <p className="label-uppercase">Shifts planifiés</p>
          </div>
          <p className="text-2xl font-display font-bold tracking-tight">{shifts.length}</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid="planning-kpi-hours">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={14} className="text-emerald-600" />
            <p className="label-uppercase">Heures planifiées</p>
          </div>
          <p className="text-2xl font-display font-bold tracking-tight">{totalHours} h</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid="planning-kpi-upcoming">
          <div className="flex items-center gap-2 mb-2">
            <CalendarDays size={14} className="text-sky-600" />
            <p className="label-uppercase">À venir</p>
          </div>
          <p className="text-2xl font-display font-bold tracking-tight">{upcoming.length}</p>
          <p className="text-[11px] text-zinc-500 font-mono mt-1">{past.length} passé{past.length > 1 ? "s" : ""}</p>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
          <p className="label-uppercase">Historique de planification</p>
          <span className="text-[11px] font-mono text-zinc-500">
            {loading ? "Chargement…" : `${shifts.length} shift${shifts.length > 1 ? "s" : ""}`}
          </span>
        </div>
        <div className="divide-y divide-zinc-100 max-h-[560px] overflow-y-auto">
          {!loading && shifts.length === 0 && (
            <p className="p-6 text-sm text-zinc-500 font-mono text-center" data-testid="planning-empty">
              Aucune planification sur la période sélectionnée.
            </p>
          )}
          {upcoming.length > 0 && (
            <div className="bg-sky-50/50 px-4 py-1.5 text-[10px] uppercase tracking-wider text-sky-700 font-mono border-b border-sky-100">
              À venir · {upcoming.length}
            </div>
          )}
          {upcoming.map((s) => renderRow(s, false))}
          {past.length > 0 && (
            <div className="bg-zinc-50 px-4 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 font-mono border-b border-zinc-100">
              Passés · {past.length}
            </div>
          )}
          {past.map((s) => renderRow(s, true))}
        </div>
      </div>
    </div>
  );
}
